import React, { useState } from 'react';
import { Headercomponent } from '../components/headercomponent';
import NodeBackground from '../components/nodeBackground';
import DemoModal from '../components/demoModal';
import Modal from 'react-modal';
import { Link } from 'react-router-dom';
import { Footer } from '../components/Footer';

Modal.setAppElement('#root');

const FounderSignalPanel = () => (
  <div className="about-founder-panel">
    <div className="about-founder-card">
      <div className="about-founder-photo-frame">
        <div className="founder-photo">
          <img
            src="/alex_farley_headshot.jpg"
            alt="Alex Farley, Founder of Weathervane Intel"
          />
        </div>
      </div>

      <div className="about-founder-identity">
        <p className="eyebrow">Founder-led intelligence</p>
        <h3>Built by an analyst who learned to build tools because the work demanded it.</h3>
        <p>
          Weathervane Intel combines protective intelligence experience, OSINT tradecraft,
          and purpose-built technology to help clients understand what matters before decisions are made.
        </p>
      </div>
    </div>

  </div>
);

export const About = () => {
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const handleModal = () => setModalIsOpen(!modalIsOpen);

  const supportModels = [
    {
      title: 'Ongoing Protective Intelligence Support',
      text:
        'For executive protection firms, family offices, and corporate security teams that need an external intelligence partner supporting recurring decisions, monitoring, and reporting.'
    },
    {
      title: 'Event and Travel Surge Capacity',
      text:
        'For moments when visibility increases and teams need additional OSINT collection, local context, online monitoring, or analyst support before and during movement.'
    },
    {
      title: 'Custom Intelligence Questions',
      text:
        'For clients who need a focused answer involving a person, place, event, organization, online exposure concern, or risk decision that does not fit a standard report.'
    }
  ];

  const principles = [
    {
      title: 'Information is not intelligence',
      text:
        'Raw data does not answer a decision-maker\'s question by itself. Intelligence requires context, verification, synthesis, and judgment across many pieces of information.'
    },
    {
      title: 'Speed matters, but so does judgment',
      text:
        'Software can reduce the manual burden of OSINT work, but meaningful findings still require an analyst who understands risk, exposure, behavior, and operational relevance.'
    },
    {
      title: 'The answer should be usable',
      text:
        'A strong intelligence product should make complex information easier to act on, not harder. The goal is clarity for the person or team making the decision.'
    }
  ];

  const teamSupport = [
    'Behind-the-scenes intelligence support for executive protection teams',
    'Travel and event intelligence for high-visibility movement',
    'Online exposure and public information reviews',
    'OSINT threat detection and analyst triage',
    'Residential and location-based crime intelligence',
    'Custom reports for specific protective or business decisions'
  ];

  const trustSignals = [
    'Public safety and investigative background',
    'Protective intelligence and OSINT experience',
    'Purpose-built software and automation capability',
    'Founder involvement in analysis and delivery',
    'Structured reporting for high-consequence decisions',
    'Boutique attention with technical depth'
  ];

  return (
    <>
      <Headercomponent />

      <div className="container about-page boutique-about-page">
        <div className="hero about-hero boutique-about-hero">
          <NodeBackground />

          <div className="hero-content">
            <p className="eyebrow">About Weathervane Intel</p>
            <h1>A Boutique Protective Intelligence Firm Built for Trust, Clarity, and Action</h1>
            <p>
              Weathervane Intel helps executive protection firms, family offices, corporate security teams,
              and high-exposure clients understand risk through analyst-led OSINT, tailored reporting,
              and purpose-built intelligence technology.
            </p>
            <p>
              The firm was built for clients who need more than search results. They need a trusted external
              intelligence partner who can collect, interpret, and explain what matters before a decision is made.
            </p>

            <div className="hero-actions">
              <button className="cta-button" onClick={handleModal}>
                Schedule a Consultation
              </button>
              <Link to="/services" className="secondary-button">
                View Services
              </Link>
            </div>
          </div>

          <div className="hero-diagram about-hero-diagram">
            <FounderSignalPanel />
          </div>
        </div>

        <section className="platform-overview about-story-section">
          <p className="eyebrow">Founder story</p>
          <h2>Built from a firsthand understanding of the OSINT capability gap</h2>
          <p>
            Weathervane Intel began after years of seeing the same problem from inside protective and investigative
            work: there is more public information available than most teams can reasonably review by hand.
            Relevant indicators may be buried across public records, social platforms, forums, comment sections,
            local data, and sources that do not surface cleanly through a standard search.
          </p>
          <p>
            Before building Weathervane Intel, founder Alex Farley served in public safety and later worked in
            protective intelligence and open-source intelligence. The work often required hundreds of searches,
            open tabs, spreadsheets of sources, and long hours reviewing online material to find the few items that
            actually mattered. That experience shaped the firms belief that technology can and should reduce the burden of collection
            and processing, while analysts apply context, judgment, and structured reporting.
          </p>
        </section>

        <section className="capability-statement about-founder-letter">
          <p className="eyebrow">A note from the founder</p>
          <h2>Why this work matters</h2>
          <p>
            I started Weathervane Intel because I believe protective intelligence should help people live, work,
            travel, and make important decisions with better awareness. No executive, family, public figure, or
            organization should have to rely on guesswork when public information may reveal exposure, risk,
            or developing concern.
          </p>
          <p>
            My background taught me that information alone is not enough. A search result is not an assessment,
            a list of links is not a threat picture, and raw data rarely tells a decision-maker what to do next.
            Weathervane Intel exists to turn public information into structured intelligence that helps clients
            understand what matters.
          </p>
          <p className="founder-signature">Alex Farley<br /><span>Founder, Weathervane Intel</span></p>
        </section>

        <section className="process-deliverables-section about-principles-section about-spaced-section">
          <div className="process-column">
            <span className="section-kicker">Our philosophy</span>
            <h2>Intelligence is the difference between data and direction</h2>
            <p>
              Weathervane Intel was built around the belief that the value of OSINT is not in collecting more
              information. The value is in helping the right person understand the right information at the right time.
            </p>
          </div>

          <div className="deliverables-column about-principles-grid">
            {principles.map((principle) => (
              <div className="deliverable-card about-principle-card" key={principle.title}>
                <h3>{principle.title}</h3>
                <p>{principle.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="platform-overview about-support-section">
          <div className="section-heading-row">
            <div>
              <p className="eyebrow">How we support teams</p>
              <h2>An External Intelligence Partner for Protective Operations</h2>
            </div>
            <Link to="/services" className="text-link">Explore services →</Link>
          </div>

          <p>
            Weathervane Intel is designed to support teams that need specialized OSINT capability without building
            an entire internal function. We can provide ongoing protective intelligence support, short-term surge
            capacity, or focused reporting for a specific event, trip, person, property, or operational question.
          </p>

          <div className="overview-cards about-support-grid">
            {supportModels.map((model) => (
              <div className="overview-feature about-support-card" key={model.title}>
                <div className="feature-text">
                  <h3>{model.title}</h3>
                  <p>{model.text}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="how-it-works about-team-support-section">
          <p className="eyebrow">Capabilities</p>
          <h2>Where Weathervane Intel Fits</h2>
          <p>
            The firm supports high-level clients and protective teams that need intelligence in the background,
            not noise in the foreground. The work is tailored to the client, the asset, and the decision being made.
          </p>

          <div className="about-capability-grid">
            {teamSupport.map((item, index) => (
              <div className="about-capability-card" key={item}>
                <span className="about-capability-number">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <p>{item}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="platform-overview about-trust-section">
          <p className="eyebrow">Why clients choose a boutique intelligence firm</p>
          <h2>Specialized Attention, Technical Capability, and Faster Pivoting</h2>
          <p>
            Large firms can bring scale. Weathervane Intel is built for clients who need specialized attention,
            technical creativity, and the ability to move quickly around a defined intelligence question. The firm
            combines analyst judgment with hand-crafted technical capability to support timely, usable answers.
          </p>

          <div className="seo-topic-grid about-trust-grid">
            {trustSignals.map((signal) => (
              <span className="seo-topic-pill about-trust-pill" key={signal}>{signal}</span>
            ))}
          </div>
        </section>

        <section className="process-deliverables-section about-method-section">
          <div>
            <p className="eyebrow">Method</p>
            <h2>How We Work</h2>
            <p>
              Each engagement begins with the decision or intelligence question the client needs answered. From there,
              we define the scope, collect and organize relevant open-source information, apply analyst review,
              and deliver findings in a format designed for action.
            </p>
          </div>

          <div className="about-method-grid">
            {[
              'Define the intelligence question',
              'Collect and organize relevant information',
              'Analyze what matters and why',
              'Deliver clear findings for decision support'
            ].map((step, index) => (
              <div className="about-method-card" key={step}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <p>{step}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="cta-section about-cta">
          <h2>Start with the Decision You Need to Make</h2>
          <p>
            If you need protective intelligence support for an executive, family, event, trip, property, or specific
            OSINT question, Weathervane Intel can help define the question and build the intelligence product around it.
          </p>
          <button className="cta-button" onClick={handleModal}>
            Schedule a Consultation
          </button>
        </section>

        <DemoModal isOpen={modalIsOpen} onClose={() => setModalIsOpen(false)} />
      </div>

      <Footer />
    </>
  );
};

export default About;
